/**
 * todo - resolve typing issues in another story
 */
import { computed, ref, shallowRef } from 'vue';
import { defineStore } from 'pinia';
import type { Review } from '~~/shared/globalTypes/reviews';
import useReviewsServiceStore from './useReviewsServiceStore';
import useReviewsStore from './useReviewsStore';
import parseProductReviewsData from '~/modules/reviews/mappers/parseProductReviewsData';
import { SORT_BY_OPTION, SORT_BY_OPTIONS_V2 } from '~/modules/reviews/constants/reviews';
import type { ReviewMedia } from '../mappers/parseReviewsMedia';

/**
 * Type definition for the detailed photo object found within a review from the `reviewsWithPhotos` endpoint.
 */
type ReviewWithPhotoDetails = {
  sizes: { normal: { url: string } };
  caption: string;
};

type ReviewWithVideoDetails = {
  caption: string;
  thumbnailUrl: string;
  videoId: string;
  url: string;
};

/**
 * Represents a Review object that includes detailed photo information.
 */
type ReviewWithPhoto = Omit<Review, 'photos'> & {
  photos?: ReviewWithPhotoDetails[];
};

const REVIEW_PHOTOS_XAPI_LIMIT = 12;

export default defineStore('reviewsPhotosStore', () => {
  const { reviewRequestService } = useReviewsServiceStore();
  // State
  /**
   * The base list of all photo objects.
   */
  const basePhotos = shallowRef<ReviewMedia['photos']>([]);
  /**
   * The list of reviews that are associated with the photos.
   * @type {import('vue').Ref<ReviewWithPhoto[]>}
   */
  const reviewsPhotos = shallowRef<ReviewWithPhoto[]>([]);
  /**
   * The total number of photos available from the API.
   * @type {import('vue').Ref<number | null>}
   */
  const totalPhotos = ref<number | null>(null);

  // Getters
  /**
   * Determines if a specific media URL exists within a given array of media data.
   * This supports matching against photo URLs, video URLs, and video thumbnails.
   * @param {string} photoUrl - The URL of the media item to search for.
   * @param {(ReviewWithPhotoDetails & ReviewWithVideoDetails)[]} mediaData - An array of photo or video objects to search through.
   * @returns {boolean} True if a match is found, false otherwise.
   */
  const findRelatedReviews = <T extends ReviewWithPhotoDetails & ReviewWithVideoDetails>(photoUrl: string, mediaData?: T[]): boolean =>
    Boolean(mediaData?.some((media) => media?.sizes?.normal?.url === photoUrl || media?.url === photoUrl || media?.thumbnailUrl === photoUrl));

  /**
   * A computed property that returns an array of photo objects, each augmented with the index of its corresponding review.
   */
  const photos = computed(() => {
    const allPhotos = basePhotos.value || [];
    return allPhotos.map((photo) => {
      const partialFindRelatedReviewsCb = findRelatedReviews.bind(null, photo.url) as <T>(mediaData?: T[]) => boolean;
      return {
        ...photo,
        reviewIndex: reviewsPhotos.value.findIndex(
          (review) => partialFindRelatedReviewsCb(review.photos) || partialFindRelatedReviewsCb(review.videos),
        ),
      };
    });
  });

  /**
   * A computed property for the total count of reviews with photos.
   * It uses the `totalPhotos` state if available, otherwise it falls back to the length of the `photos` array.
   * @type {import('vue').ComputedRef<number>}
   */
  const totalReviewsPhotosCount = computed<number>(() => totalPhotos.value ?? photos.value.length);

  /**
   * A computed property that returns true if there are any videos in the media list.
   */
  const hasVideos = computed(() => photos.value.some((item) => item.type === 'video'));

  /**
   * @description determines if all photos have already been lazy loaded or not
   * False when...
   * - photos amount has reached max capacity (at or above photos total limit)
   * True when...
   * - below photos total limit
   *  */
  const photosAlreadyLazyLoaded = computed(
    () => photos.value.length >= totalReviewsPhotosCount.value, // photos count in carousel list exceeds total count
  );

  // Actions
  /**
   * Resets all photo-related state to its initial values.
   */
  function resetPhotos(): void {
    basePhotos.value = [];
    reviewsPhotos.value = [];
    totalPhotos.value = null;
  }

  /**
   * Updates the store's state with new photos and their associated reviews.
   * @param reviewsWithPhotos - The object containing new photos and reviews.
   */
  function updatePhotos(reviewsWithPhotos: ReviewMedia): void {
    totalPhotos.value ??= reviewsWithPhotos.totalPhotos;
    basePhotos.value = [...basePhotos.value, ...reviewsWithPhotos.photos];
    reviewsPhotos.value = [...reviewsPhotos.value, ...reviewsWithPhotos.reviews];
  }

  /**
   * Fetches reviews that contain photos from the API.
   * @param {number} offset - The offset for pagination.
   */
  async function fetchReviewsWithPhotos(offset: number): Promise<void> {
    const productId = useReviewsStore().activeReviewProductId as string;
    return reviewRequestService({
      productId,
      params: {
        photosWithReviews: true,
        sort: SORT_BY_OPTIONS_V2[SORT_BY_OPTION.NEWEST],
        limitPhotos: REVIEW_PHOTOS_XAPI_LIMIT,
        offsetPhotos: offset,
      },
    })
      .then((response) => {
        const { reviewsWithPhotos } = parseProductReviewsData({
          //todo - resolve typing properly
          product: 'data' in response ? response.data : response,
          productId,
        });

        if (reviewsWithPhotos) {
          updatePhotos(reviewsWithPhotos);
        }
      })
      .catch((err: Error) => {
        console.error(`Couldn't fetch reviews with photos: ${err}`);
      });
  }

  return {
    // State
    reviewsPhotos,
    basePhotos,
    totalPhotos,
    hasVideos,
    // Getters
    photos,
    totalReviewsPhotosCount,
    photosAlreadyLazyLoaded,
    // Actions
    fetchReviewsWithPhotos,
    resetPhotos,
    updatePhotos,
  };
});
